import { db } from './config';
import { collection, doc, getDocs, addDoc, updateDoc, deleteDoc, query, where, orderBy, onSnapshot, type FirestoreError, type Unsubscribe } from 'firebase/firestore';
import { Prescription, PrescriptionExecutionStatus } from '@/types/models';

/** Item within an exam request (SADT guide line) */
export interface ExamItem {
  name: string;
  code?: string;
  justification: string;
  cid: string;
}

/**
 * Remove campos com valor `undefined` de um objeto recursivamente — necessário porque o
 * Firestore rejeita "Unsupported field value: undefined". Tipos opcionais em TS frequentemente
 * resultam em campos undefined que precisam ser limpados antes de chegar no setDoc/addDoc.
 */
function stripUndefined<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  // Recursivo de verdade: `content.medications[i].prescriptionType = undefined`
  // (vindo do banco curado) derrubava o addDoc da receita ditada (16/09/2026).
  return stripDeep(obj) as Record<string, unknown>;
}

function stripDeep(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(stripDeep);
  if (v && typeof v === 'object' && Object.getPrototypeOf(v) === Object.prototype) {
    return Object.fromEntries(
      Object.entries(v as Record<string, unknown>)
        .filter(([, x]) => x !== undefined)
        .map(([k, x]) => [k, stripDeep(x)]),
    );
  }
  return v;
}

/**
 * Tipos de procedimento que entram na fila de "Procedimentos em atraso"
 * (Caixa de Pendências). Outros tipos (receita de óculos, medicamento,
 * atestado, etc.) nunca recebem `executionStatus` — não fazem sentido nessa
 * lógica.
 */
const TRACKABLE_EXECUTION_TYPES: ReadonlySet<Prescription['type']> = new Set([
  'SURGERY',
  'EXAM_REQUEST',
]);

export function isTrackableProcedure(type: Prescription['type']): boolean {
  return TRACKABLE_EXECUTION_TYPES.has(type);
}

/**
 * Monta o documento exatamente como `createPrescription` grava — pra quem
 * precisa escrever numa `writeBatch` (ex.: registro do ditado, spec 009)
 * sem duplicar as regras abaixo.
 *
 * SURGERY e EXAM_REQUEST nascem com executionStatus=PENDING para alimentar
 * a Caixa de Pendências. Quem chamou pode sobrescrever explicitamente
 * (ex.: importar histórico antigo como DONE).
 */
export function buildPrescriptionDoc(data: Omit<Prescription, 'id'>): Record<string, unknown> {
  const enriched: Omit<Prescription, 'id'> = isTrackableProcedure(data.type) && !data.executionStatus
    ? { ...data, executionStatus: 'PENDING' }
    : data;
  return stripUndefined(enriched as unknown as Record<string, unknown>);
}

export const PrescriptionService = {
  async createPrescription(clinicId: string, data: Omit<Prescription, 'id'>): Promise<string> {
    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const docRef = await addDoc(colRef, buildPrescriptionDoc(data));
    return docRef.id;
  },

  async getPrescriptionsByPatient(clinicId: string, patientId: string): Promise<Prescription[]> {
    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const q = query(colRef, where('patientId', '==', patientId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as Prescription));
  },

  /**
   * `getPrescriptionsByPatient` AO VIVO: a lista inteira na primeira leitura
   * e de novo a cada documento gravado, editado ou excluído (ditado, outra
   * máquina…).
   */
  subscribeByPatient(
    clinicId: string,
    patientId: string,
    callback: (prescriptions: Prescription[]) => void,
    onError?: (err: FirestoreError) => void,
  ): Unsubscribe {
    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const q = query(colRef, where('patientId', '==', patientId), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) =>
        callback(
          snap.docs.map(d => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) } as Prescription)),
        ),
      (err) => onError?.(err),
    );
  },

  /**
   * Lista prescrições criadas no intervalo [startMs, endMs] (epoch ms),
   * opcionalmente filtradas por tipo. Usado pelos relatórios clínicos
   * (Prescrições emitidas / Exames solicitados).
   */
  async listByCreatedAtRange(
    clinicId: string,
    startMs: number,
    endMs: number,
    type?: Prescription['type'],
  ): Promise<Prescription[]> {
    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const q = type
      ? query(
          colRef,
          where('type', '==', type),
          where('createdAt', '>=', startMs),
          where('createdAt', '<=', endMs),
        )
      : query(
          colRef,
          where('createdAt', '>=', startMs),
          where('createdAt', '<=', endMs),
        );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Prescription));
  },

  /**
   * Retorna SURGERY/EXAM_REQUEST com `executionStatus == PENDING` da clínica.
   * O filtro por "está em atraso" (createdAt < cutoff) é aplicado em memória
   * porque o Firestore não compõe múltiplos range filters facilmente — o
   * volume típico (centenas de docs PENDING) cabe num getDocs sem paginar.
   */
  async listPendingProcedures(clinicId: string): Promise<Prescription[]> {
    const colRef = collection(db, 'clinics', clinicId, 'prescriptions');
    const q = query(
      colRef,
      where('type', 'in', ['SURGERY', 'EXAM_REQUEST']),
      where('executionStatus', '==', 'PENDING'),
      orderBy('createdAt', 'asc'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as Prescription));
  },

  /**
   * Marca um procedimento como realizado/cancelado. Não usa updatePrescription
   * porque a regra Firestore genérica de prescriptions exige janela de 24h —
   * para o status de execução abrimos exceção explícita nas rules.
   */
  async setExecutionStatus(
    clinicId: string,
    prescriptionId: string,
    status: PrescriptionExecutionStatus,
    actorUid: string,
    options?: { note?: string; executedAt?: number },
  ): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'prescriptions', prescriptionId);
    const payload: Record<string, unknown> = {
      executionStatus: status,
      executedBy: actorUid,
      executedAt: status === 'DONE' ? options?.executedAt ?? Date.now() : Date.now(),
    };
    if (options?.note && options.note.trim()) {
      payload.executionNote = options.note.trim();
    }
    await updateDoc(docRef, payload);
  },

  async updatePrescription(clinicId: string, id: string, data: Partial<Prescription>): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'prescriptions', id);
    await updateDoc(docRef, stripUndefined(data as unknown as Record<string, unknown>));
  },

  /**
   * Define (ou limpa, com string vazia) o rótulo de exibição da prescrição/
   * documento na coluna do prontuário. Update MÍNIMO (só `displayTitle`),
   * liberado mesmo após 24h pela regra `isDisplayTitleUpdate()` — não usa
   * `updatePrescription` porque a regra padrão exige a janela de 24h.
   */
  async setDisplayTitle(clinicId: string, id: string, title: string): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'prescriptions', id);
    await updateDoc(docRef, { displayTitle: title.trim() });
  },

  async deletePrescription(clinicId: string, id: string): Promise<void> {
    const docRef = doc(db, 'clinics', clinicId, 'prescriptions', id);
    await deleteDoc(docRef);
  },

  /**
   * Creates ONE exam-request prescription with the full exam list.
   * Returns the created prescription IDs (array de 1, mantido pela
   * compatibilidade de assinatura com os chamadores).
   *
   * Histórico: até ago/2026 os exames eram divididos em blocos de 5
   * ("guias SADT" — limite de linhas do formulário TISS SP/SADT), criando
   * VÁRIOS registros no prontuário pra uma única solicitação. O limite de 5
   * é uma regra de IMPRESSÃO do formulário TISS (que hoje nem é usado nos
   * impressos — a impressão lista todos os exames numa tabela única), não
   * do dado: o registro clínico é um só. Registros antigos com
   * `guideNumber`/`totalGuides` continuam sendo exibidos normalmente.
   */
  async createExamRequest(
    clinicId: string,
    baseData: Omit<Prescription, 'id' | 'content'>,
    items: ExamItem[],
    notes?: string,
    /** Liga este pedido ao pedido cirúrgico do mesmo salvamento (entrada única no prontuário). */
    requestGroupId?: string
  ): Promise<string[]> {
    const trimmedNotes = notes?.trim();
    const prescriptionData: Omit<Prescription, 'id'> = {
      ...baseData,
      content: {
        exams: items,
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        ...(requestGroupId ? { requestGroupId } : {}),
      },
    };
    const id = await this.createPrescription(clinicId, prescriptionData);
    return [id];
  },
};
